import { axios, AxiosResponse } from "@sdk/axios";
import { SDKConfig } from "@sdk/default-sdk-config";
import { EntityServices } from "@sdk/utils/entity.service";
import { PaginatedOptions, PaginatedResults } from "@sdk/utils/paginated-results";
import {
  iMetaCustomAudience,
  iMetaCustomAudienceRecord,
  MetaCustomAudienceCompileResponse,
  MetaCustomAudienceSyncResponse,
  MetaCustomAudienceSyncStatusResponse,
} from "./meta-custom-audiences.models";
import {
  MetaAdAccountsResponse,
  MetaAdInsightsResponse,
  MetaAudiencesOnMetaResponse,
  MetaCampaignInsightsResponse,
  MetaInsightsParams,
  MetaOverviewResponse,
} from "./meta-marketing.models";

export class MetaMarketingServices {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
    this.metaCustomAudiences.configure(config);
  }

  constructor(protected config: { basePath: string; token: string }) {
    this.metaCustomAudiences.configure(config);
  }

  metaCustomAudiences = Object.assign(
    new EntityServices<iMetaCustomAudience>(SDKConfig, "meta-custom-audiences"),
    {
      compile: async (id: string): Promise<MetaCustomAudienceCompileResponse> => {
        const results: AxiosResponse<MetaCustomAudienceCompileResponse> =
          await axios.post(
            this.config.basePath + `/meta-custom-audiences/${id}/compile`,
            {},
            {
              headers: {
                Authorization: this.config.token,
              },
            },
          );
        return results.data;
      },

      sync: async (
        id: string,
        body: { adAccountId: string; connectionId?: string },
      ): Promise<MetaCustomAudienceSyncResponse> => {
        const results: AxiosResponse<MetaCustomAudienceSyncResponse> =
          await axios.post(
            this.config.basePath + `/meta-custom-audiences/${id}/sync`,
            body,
            {
              headers: {
                Authorization: this.config.token,
              },
            },
          );
        return results.data;
      },

      getSyncStatus: async (
        id: string,
      ): Promise<MetaCustomAudienceSyncStatusResponse> => {
        const results: AxiosResponse<MetaCustomAudienceSyncStatusResponse> =
          await axios.get(
            this.config.basePath + `/meta-custom-audiences/${id}/sync-status`,
            {
              headers: {
                Authorization: this.config.token,
              },
            },
          );
        return results.data;
      },

      queryRecords: async (
        id: string,
        body: {
          query?: { status?: "COMPILED" | "SKIPPED" };
          compileRunId?: string;
          options: PaginatedOptions;
        },
      ): Promise<
        PaginatedResults<iMetaCustomAudienceRecord> & { compileRunId?: string }
      > => {
        const results: AxiosResponse<
          PaginatedResults<iMetaCustomAudienceRecord> & {
            compileRunId?: string;
          }
        > = await axios.post(
          this.config.basePath + `/meta-custom-audiences/${id}/records/query`,
          body,
          {
            headers: {
              Authorization: this.config.token,
            },
          },
        );
        return results.data;
      },
    },
  ) as EntityServices<iMetaCustomAudience> & {
    compile: (id: string) => Promise<MetaCustomAudienceCompileResponse>;
    sync: (
      id: string,
      body: { adAccountId: string; connectionId?: string },
    ) => Promise<MetaCustomAudienceSyncResponse>;
    getSyncStatus: (id: string) => Promise<MetaCustomAudienceSyncStatusResponse>;
    queryRecords: (
      id: string,
      body: {
        query?: { status?: "COMPILED" | "SKIPPED" };
        compileRunId?: string;
        options: PaginatedOptions;
      },
    ) => Promise<
      PaginatedResults<iMetaCustomAudienceRecord> & { compileRunId?: string }
    >;
  };

  metaMarketing = {
    getAdAccounts: async (): Promise<MetaAdAccountsResponse> => {
      const results: AxiosResponse<MetaAdAccountsResponse> = await axios.get(
        this.config.basePath + "/meta-marketing/ad-accounts",
        {
          headers: {
            Authorization: this.config.token,
          },
        },
      );
      return results.data;
    },

    getOverview: async (
      params?: MetaInsightsParams,
    ): Promise<MetaOverviewResponse> => {
      const results: AxiosResponse<MetaOverviewResponse> = await axios.get(
        this.config.basePath + "/meta-marketing/overview",
        {
          headers: {
            Authorization: this.config.token,
          },
          params,
        },
      );
      return results.data;
    },

    getCampaignInsights: async (
      params?: MetaInsightsParams,
    ): Promise<MetaCampaignInsightsResponse> => {
      const results: AxiosResponse<MetaCampaignInsightsResponse> =
        await axios.get(
          this.config.basePath + "/meta-marketing/insights/campaigns",
          {
            headers: {
              Authorization: this.config.token,
            },
            params,
          },
        );
      return results.data;
    },

    getAdInsights: async (
      params?: MetaInsightsParams,
    ): Promise<MetaAdInsightsResponse> => {
      const results: AxiosResponse<MetaAdInsightsResponse> = await axios.get(
        this.config.basePath + "/meta-marketing/insights/ads",
        {
          headers: {
            Authorization: this.config.token,
          },
          params,
        },
      );
      return results.data;
    },

    getAudiencesOnMeta: async (
      adAccountId: string,
      connectionId?: string,
    ): Promise<MetaAudiencesOnMetaResponse> => {
      const results: AxiosResponse<MetaAudiencesOnMetaResponse> =
        await axios.get(
          this.config.basePath +
            `/meta-marketing/ad-accounts/${encodeURIComponent(adAccountId)}/audiences`,
          {
            headers: {
              Authorization: this.config.token,
            },
            params: connectionId ? { connectionId } : undefined,
          },
        );
      return results.data;
    },
  };
}
